import { CademyError } from '@shared/domain-shared';
import { FileUploadQuizQuestion } from '@shared/types';
import {
    Alert,
    FileCard,
    FileRejection,
    FileRejectionReason,
    FileUploader,
    majorScale,
    Pane,
    rebaseFiles,
    Text,
} from 'evergreen-ui';
import React, { Fragment } from 'react';
import { FunctionComponent, useCallback, useMemo, useRef, useState } from 'react';
import { useCademyAPIMutation } from 'services/CademyAPI/useCademyAPIMutation';
import { Feedback } from '../../Feedback';
import { QuestionLabel } from '../../QuestionLabel';
import { BaseQuestionProps, FileAnswer } from '../../types';

type FileUploadQuestionProps = BaseQuestionProps & {
    quizId: string;
    question: FileUploadQuizQuestion;
};
export const FileUploadQuestion: FunctionComponent<FileUploadQuestionProps> = ({
    quizId,
    question,
    studentAnswers,
    setStudentAnswers,
    quizDisabled,
    markedResult,
    feedbackCorrect,
    feedbackWrong,
    onQuestionBlur,
}) => {
    const formPickerRef = useRef<HTMLFormElement>(null);

    const { mutateAsync: getUploadUrl } = useCademyAPIMutation(
        'QuizResponsesGetAttachmentUploadUrl'
    );
    const [filesUploading, setFilesUploading] = useState<Record<string, boolean>>({});
    const [errors, setErrors] = useState<string[]>([]);
    const maxSizeInBytes = useMemo(() => question.max_file_size * 1024 ** 2, [question]);

    const [files, setFiles] = useState<File[]>([]);
    const [fileRejections, setFileRejections] = useState<FileRejection[]>([]);
    const values = useMemo(
        () => [...files, ...fileRejections.map((fileRejection) => fileRejection.file)],
        [files, fileRejections]
    );

    const handleFileUpload = useCallback(
        async (files: File[]) => {
            const currentNumberOfFiles =
                (studentAnswers[question._id] as FileAnswer[])?.length || 0;
            const numberToUpload = files.length;
            if (currentNumberOfFiles + numberToUpload > question.max_files) {
                setErrors([
                    `You can only upload ${question.max_files} file${question.max_files === 1 ? '' : 's'}. You have already uploaded ${currentNumberOfFiles}.`,
                ]);
                if (formPickerRef.current) {
                    formPickerRef.current.reset();
                }
                return;
            }
            setErrors([]);
            try {
                const uploadedFileReferences = await Promise.all(
                    Array.from(files).map(async (file): Promise<FileAnswer> => {
                        try {
                            setFilesUploading((prev) => ({
                                ...prev,
                                [file.name]: true,
                            }));
                            const fileSize = file.size;
                            const { url, reference } = await getUploadUrl({
                                queryParams: {
                                    quiz: quizId,
                                    question: question._id,
                                    'file-size': fileSize,
                                },
                            });

                            const uploadResponse = await fetch(url, {
                                method: 'PUT',
                                body: file,
                            });

                            if (!uploadResponse.ok) {
                                throw new CademyError(
                                    'upload/error',
                                    `Failed to upload file: ${file.name}.`,
                                    undefined,
                                    400
                                );
                            }

                            return {
                                fileReference: reference,
                                fileName: file.name,
                                fileSize: file.size,
                                type: file.type,
                            };
                        } finally {
                            setFilesUploading((prev) => ({
                                ...prev,
                                [file.name]: false,
                            }));
                        }
                    })
                );

                setStudentAnswers((prev) => ({
                    ...prev,
                    [question._id]: [
                        ...((prev[question._id] as FileAnswer[]) || []),
                        ...uploadedFileReferences,
                    ],
                }));
            } catch (error) {
                console.error(error);
                if (error instanceof CademyError) {
                    setErrors([error.title]);
                } else {
                    setErrors([`An unexpected error occurred`]);
                }
            }
        },
        [getUploadUrl, question, quizId, setStudentAnswers, studentAnswers]
    );

    const fileAnswers = useMemo(() => {
        return (studentAnswers[question._id] as FileAnswer[]) || [];
    }, [studentAnswers, question]);

    const needMoreFiles = useMemo(() => {
        return fileAnswers.length < question.min_files;
    }, [fileAnswers, question]);

    const removeFile = useCallback(
        (file: File) => {
            const updatedFiles = files.filter((existingFile) => existingFile !== file);
            const updatedFileRejections = fileRejections.filter(
                (fileRejection) => fileRejection.file !== file
            );

            const { accepted, rejected } = rebaseFiles(
                [
                    ...updatedFiles,
                    ...updatedFileRejections.map((fileRejection) => fileRejection.file),
                ],
                { acceptedMimeTypes: [], maxFiles: question.max_files, maxSizeInBytes }
            );

            setStudentAnswers((prev) => {
                const answerWithoutFile = (prev[question._id] as FileAnswer[]).filter(
                    (answer) => answer.fileName !== file.name
                );
                if (answerWithoutFile.length === 0) {
                    const { [question._id]: _, ...rest } = prev;
                    return rest;
                }
                return {
                    ...prev,
                    [question._id]: answerWithoutFile,
                };
            });

            setFiles(accepted);
            setFileRejections(rejected);
        },
        [files, fileRejections, question.max_files, question._id, maxSizeInBytes, setStudentAnswers]
    );

    const removeSavedFile = useCallback(
        (file: FileAnswer) => {
            const updatedFiles = fileAnswers.filter(
                (existingFile) => existingFile.fileReference !== file.fileReference
            );
            setStudentAnswers((prev) => {
                const { [question._id]: _, ...rest } = prev;
                if (updatedFiles.length === 0) {
                    return rest;
                }
                return {
                    ...prev,
                    [question._id]: updatedFiles,
                };
            });
        },
        [fileAnswers, question._id, setStudentAnswers]
    );

    const fileCountOverLimit = useMemo(() => {
        return files.length + fileRejections.length - question.max_files;
    }, [files, fileRejections, question.max_files]);

    const handleAcceptedFiles = useCallback(
        (acceptedFiles: File[]) => {
            setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
            handleFileUpload(acceptedFiles);
        },
        [handleFileUpload]
    );

    const savedFilesNotInMemory = useMemo(() => {
        return fileAnswers.filter(
            (fileAnswer) => !files.some((file) => fileAnswer.fileName === file.name)
        );
    }, [fileAnswers, files]);

    return (
        <Pane key={question._id} marginY={15} onBlur={onQuestionBlur}>
            <QuestionLabel label={question.question} />
            {quizDisabled ? (
                <>
                    {fileAnswers.map((file) => {
                        return (
                            <FileCard
                                key={file.fileReference || file.fileName}
                                name={file.fileName}
                                sizeInBytes={file.fileSize}
                                type={file.type}
                            />
                        );
                    })}
                </>
            ) : (
                <>
                    <FileUploader
                        acceptedMimeTypes={[]}
                        description={`${question.min_files > 0 ? `You must upload at least ${question.min_files} file${question.min_files === 1 ? '' : 's'}. ` : ''}You can upload up to ${question.max_files} files. Files can be up to ${question.max_file_size}MB.`}
                        disabled={files.length + fileRejections.length >= question.max_files}
                        maxSizeInBytes={maxSizeInBytes}
                        maxFiles={question.max_files}
                        onAccepted={handleAcceptedFiles}
                        onRejected={setFileRejections}
                        renderFile={(file, index) => {
                            const { name, size, type } = file;
                            const renderOverFileCountError = index === 0 && fileCountOverLimit > 0;

                            const fileRejection = fileRejections.find(
                                (fileRejection) =>
                                    fileRejection.file === file &&
                                    fileRejection.reason !== FileRejectionReason.OverFileLimit
                            );
                            const { message } = fileRejection || {};

                            return (
                                <Fragment key={`${file.name}-${index}`}>
                                    {renderOverFileCountError && (
                                        <Alert
                                            intent="danger"
                                            marginBottom={majorScale(2)}
                                            title={`You can upload up to ${question.max_files} files. Please remove ${fileCountOverLimit} ${
                                                fileCountOverLimit === 1 ? 'file' : 'files'
                                            }.`}
                                        />
                                    )}

                                    <FileCard
                                        isInvalid={fileRejection != null}
                                        name={name}
                                        onRemove={() => removeFile(file)}
                                        sizeInBytes={size}
                                        type={type}
                                        validationMessage={message}
                                        isLoading={filesUploading[name] || false}
                                    />
                                </Fragment>
                            );
                        }}
                        values={values}
                    />
                    {savedFilesNotInMemory.map((file) => {
                        return (
                            <FileCard
                                key={file.fileReference || file.fileName}
                                name={file.fileName}
                                sizeInBytes={file.fileSize}
                                type={file.type}
                                onRemove={() => removeSavedFile(file)}
                            />
                        );
                    })}
                </>
            )}
            {errors.length > 0 ? (
                <Alert intent="danger" marginTop={10}>
                    {errors.map((error) => (
                        <Text key={error}>{error}</Text>
                    ))}
                </Alert>
            ) : null}
            {needMoreFiles && !Object.values(filesUploading).some(Boolean) ? (
                <Alert intent="warning" marginTop={10}>
                    {`You need to upload at least ${question.min_files} file${
                        question.min_files === 1 ? '' : 's'
                    } but you have uploaded ${fileAnswers.length} file${
                        fileAnswers.length === 1 ? '' : 's'
                    }.`}
                </Alert>
            ) : null}
            <Feedback feedbackCorrect={feedbackCorrect} feedbackWrong={feedbackWrong} />
        </Pane>
    );
};
